import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, Plus } from "../components/icons";
import CCSidebar from "../components/CCSidebar";
import StatCards from "../components/StatCards";
import PatientTable from "../components/PatientTable";
import RegisterModal from "../components/RegisterModal";
import { listProgramPatients, extractArray } from "../data/ccProgram";
import { listPatientsByCareCoordinator } from "../data/admindata";
import "../styles/ProgramOverview.css";

const PHASE_OPTIONS      = ["All Phases", "Phase 1", "Phase 2", "Phase 3"];
const STATUS_OPTIONS     = ["All Status", "Active", "On-boarding", "Needs Attention", "Completed"];
const CONDITION_OPTIONS  = ["All Conditions"];

export default function ProgramOverview() {
  const navigate  = useNavigate();
  const location  = useLocation();

  // ── Admin context (set when coming from CareCoordinatorList) ──────────────
  const isAdmin             = location.state?.isAdmin            || false;
  const careCoordinatorId   = location.state?.careCoordinatorId  || null;
  const careCoordinatorName = location.state?.careCoordinatorName || "Care Coordinator";

  const [patients,       setPatients]        = useState([]);
  const [filtered,       setFiltered]        = useState([]);
  const [search,         setSearch]          = useState("");
  const [phaseFilter,    setPhaseFilter]     = useState("All Phases");
  const [statusFilter,   setStatusFilter]    = useState("All Status");
  const [conditionFilter,setConditionFilter] = useState("All Conditions");
  const [loading,        setLoading]         = useState(true);
  const [error,          setError]           = useState(null);
  const [showModal,      setShowModal]       = useState(false);

  const loadPatients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Admin: fetch patients under the selected care coordinator
      // Caretaker: fetch own patients as before
      const raw  = isAdmin && careCoordinatorId
        ? await listPatientsByCareCoordinator(careCoordinatorId)
        : await listProgramPatients();

      const data = extractArray(raw);

      const enrolled = data.filter(
        (p) => p && p.tasklist_id && typeof p.tasklist_id === "string" && p.tasklist_id.length > 0
      );

      console.log(`[ProgramOverview] total: ${data.length}, enrolled: ${enrolled.length}, isAdmin: ${isAdmin}`);

      setPatients(enrolled);
      setFiltered(enrolled);
    } catch (err) {
      console.error("loadPatients error:", err);
      setError("Failed to load patients. Please try again.");
      setPatients([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, careCoordinatorId]);

  useEffect(() => { loadPatients(); }, [loadPatients]);

  // Dynamically collect condition options from loaded patients
  const conditionOptions = useMemo(() => {
    const set = new Set(CONDITION_OPTIONS);
    patients.forEach((p) => p?.condition && set.add(p.condition));
    return Array.from(set);
  }, [patients]);

  /* ── Client-side search + filters ── */
  useEffect(() => {
    if (!Array.isArray(patients)) return;
    const q = search.trim().toLowerCase();

    const result = patients.filter((p) => {
      const matchesSearch = q
        ? (p?.user_profile?.name || "").toLowerCase().includes(q) ||
          (p?.patient_id || "").toLowerCase().includes(q) ||
          (p?.user_profile?.phone_no || "").includes(search.trim())
        : true;

      const matchesPhase =
        phaseFilter === "All Phases" ? true : (p?.current_phase || "") === phaseFilter;

      const matchesStatus =
        statusFilter === "All Status"
          ? true
          : (p?.status || "").toLowerCase().replace(/[\s-]/g, "_") ===
            statusFilter.toLowerCase().replace(/[\s-]/g, "_");

      const matchesCondition =
        conditionFilter === "All Conditions" ? true : (p?.condition || "") === conditionFilter;

      return matchesSearch && matchesPhase && matchesStatus && matchesCondition;
    });

    setFiltered(result);
  }, [search, phaseFilter, statusFilter, conditionFilter, patients]);

  /* ── Stats ── */
  const stats = {
    total:          patients.length,
    active:         patients.filter((p) => p?.status === "active").length,
    completed:      patients.filter((p) => p?.status === "completed").length,
    needsAttention: patients.filter((p) => p?.status === "needs_attention").length,
  };

  const handlePatientClick = (patient) => {
    navigate(`/care-coordinator/patient/${patient.patient_id}`, {
      state: {
        patient,
        // Pass admin context down to PatientDetail
        isAdmin,
        careCoordinatorId,
        careCoordinatorName,
      },
    });
  };

  return (
    <div className="cc-overview-root">
      <CCSidebar
        backLink={isAdmin ? { label: "All Care Coordinators", to: "/admin/care-coordinators" } : null}
      />

      <main className="cc-overview-main">
        <div className="cc-page-header">
          <h1 className="cc-page-title">
            {isAdmin ? `${careCoordinatorName}'s Patients` : "Patient Programme Overview"}
          </h1>
          <p className="cc-page-subtitle">
            {isAdmin
              ? `Viewing patients under ${careCoordinatorName}`
              : "View enrolled patients, track programme progress, current phase, and care status in one place."}
          </p>
        </div>

        <StatCards stats={stats} />

        <div className="cc-table-section">
          <div className="cc-table-header">
            <div className="cc-search-wrap">
              <Search size={15} className="cc-search-icon" />
              <input
                className="cc-search-input"
                placeholder="Search by name, mobile number or patient id"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="cc-filters">
              <select className="cc-filter-select" value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}>
                {PHASE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              <select className="cc-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                {STATUS_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              <select className="cc-filter-select" value={conditionFilter} onChange={(e) => setConditionFilter(e.target.value)}>
                {conditionOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>

            {/* Hide "Add Patient" button for admin — read-only view */}
            {!isAdmin && (
              <button className="cc-add-btn" onClick={() => setShowModal(true)}>
                <Plus size={16} /> Add Patient
              </button>
            )}
          </div>

          {error && <div className="cc-table-error">{error}</div>}

          <PatientTable
            patients={filtered}
            loading={loading}
            onRowClick={handlePatientClick}
          />
        </div>
      </main>

      {showModal && (
        <RegisterModal
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            loadPatients();
          }}
        />
      )}
    </div>
  );
}